import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Camera, Check, CloudOff, Keyboard, RefreshCw, UserCheck, Users, X,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { enqueue, flush, pendingCount } from "../lib/scanQueue";
import CameraScanner, { cameraSupported } from "../components/CameraScanner";

/**
 * Check-in station.
 *
 * The hardest screen in this product: used at a door, in bad light, by someone
 * who is not looking at it carefully, with a queue forming behind the person in
 * front of them.
 *
 * Rules that follow:
 *   - the result fills the screen and is readable at arm's length
 *   - green / amber / red are distinguishable at a glance, and each carries an
 *     icon and words as well as colour
 *   - manual entry is always available, because cameras fail
 *   - the phone camera can read the ticket QR; it feeds the same submit path
 *   - nothing ever blocks on the network; every scan is queued locally first
 *   - the queue depth is always visible, so nobody packs up with scans pending
 */

const RESULT_STYLES = {
  ok: { tone: "bg-emerald-500 text-white", icon: Check, title: "Checked in" },
  duplicate: { tone: "bg-amber-500 text-white", icon: AlertTriangle, title: "Already checked in" },
  not_found: { tone: "bg-destructive text-white", icon: X, title: "Not recognised" },
  queued: { tone: "bg-blue-500 text-white", icon: CloudOff, title: "Saved — offline" },
};

export default function ScannerPage() {
  const { eventId } = useParams();
  const { authAxios } = useAuth();
  const inputRef = useRef(null);

  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [queued, setQueued] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);

  const refreshQueue = useCallback(() => setQueued(pendingCount(eventId)), [eventId]);

  const loadStats = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/events/${eventId}/check-in/stats`);
      setStats(res.data);
    } catch {
      // Stats are a nicety. Never let them interrupt scanning.
    }
  }, [authAxios, eventId]);

  const sync = useCallback(async () => {
    if (pendingCount(eventId) === 0) return;
    setSyncing(true);
    try {
      const api = authAxios();
      const res = await flush(eventId, (payload) =>
        api.post(`/events/${eventId}/check-in/batch`, payload).then((r) => r.data)
      );
      if (res.flushed) {
        toast.success(`${res.flushed} queued scan${res.flushed === 1 ? "" : "s"} synced`);
        loadStats();
      }
    } catch {
      toast.error("Still offline — scans are saved");
    } finally {
      setSyncing(false);
      refreshQueue();
    }
  }, [authAxios, eventId, loadStats, refreshQueue]);

  useEffect(() => {
    refreshQueue();
    loadStats();
  }, [refreshQueue, loadStats]);

  // Connectivity changes are the moment to drain the queue.
  useEffect(() => {
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, [sync]);

  const submit = async (raw) => {
    const value = String(raw || "").trim();
    if (!value) return;
    setCode("");
    if (!cameraOn) inputRef.current?.focus();

    // Queue first, always. The operator must never wait on the network.
    const entry = enqueue({ eventId, code: value });
    refreshQueue();

    if (!navigator.onLine) {
      setResult({ result: "queued", message: `${entry.code} saved — will sync` });
      return;
    }

    try {
      const api = authAxios();
      const res = await api.post(`/events/${eventId}/check-in`, {
        code: entry.code,
        client_scan_id: entry.client_scan_id,
      });
      // Landed live, so it no longer needs replaying.
      const { resolve } = await import("../lib/scanQueue");
      resolve([entry.client_scan_id]);
      refreshQueue();
      setResult(res.data);
      loadStats();
    } catch {
      setResult({ result: "queued", message: `${entry.code} saved — will sync` });
      setOnline(false);
    }
  };

  const style = RESULT_STYLES[result?.result] || null;
  const Icon = style?.icon;

  return (
    <div className="p-4 max-w-lg mx-auto space-y-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
          <UserCheck className="h-5 w-5" />
          Check-in
        </h1>
        <div className="flex items-center gap-2">
          {!online && (
            <span className="text-xs font-medium text-amber-600 flex items-center gap-1"
                  data-testid="scanner-offline">
              <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />Offline
            </span>
          )}
          {queued > 0 && (
            <Button size="sm" variant="outline" onClick={sync} disabled={syncing}
                    className="h-9 gap-1.5" data-testid="scanner-sync">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
              {queued} queued
            </Button>
          )}
        </div>
      </div>

      {/* The result panel is the screen. Everything else is secondary. */}
      <Card className={style ? `${style.tone} border-0` : ""}
            role="status" aria-live="assertive" data-testid="scanner-result">
        <CardContent className="p-6 min-h-[9rem] flex flex-col items-center justify-center text-center gap-2">
          {result ? (
            <>
              <Icon className="h-10 w-10" />
              <p className="text-xl font-bold">{style.title}</p>
              <p className="text-sm opacity-90">{result.message}</p>
              {result.attendee?.party_size > 1 && (
                <p className="text-sm font-semibold flex items-center gap-1.5">
                  <Users className="h-4 w-4" aria-hidden="true" />{result.attendee.party_size} people
                </p>
              )}
              {result.dietary?.length > 0 && (
                <p className="text-xs font-medium bg-black/15 rounded-full px-2.5 py-1">
                  {result.dietary.join(" · ")}
                </p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground text-sm">
              Scan a ticket, or type the code below.
            </p>
          )}
        </CardContent>
      </Card>

      {cameraOn ? (
        <CameraScanner onCode={submit} onClose={() => setCameraOn(false)} />
      ) : (
        cameraSupported() && (
          <Button variant="outline" className="w-full h-14 gap-2 text-base"
                  onClick={() => setCameraOn(true)} data-testid="scanner-camera">
            <Camera className="h-5 w-5" aria-hidden="true" />
            Scan with camera
          </Button>
        )
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Keyboard className="h-3.5 w-3.5" />
            Ticket code
          </label>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              aria-label="Ticket code"
              value={code}
              autoFocus
              autoCapitalize="characters"
              autoComplete="off"
              placeholder="ABCD1234"
              className="h-14 text-2xl font-mono tracking-[0.2em] text-center uppercase"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && submit(code)}
              data-testid="scanner-input"
            />
            <Button className="h-14 px-6" onClick={() => submit(code)}
                    data-testid="scanner-submit">
              Check in
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            A hardware scanner types the code and presses Enter — it works here with
            no extra setup.
          </p>
        </CardContent>
      </Card>

      {stats && (
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="scanner-stats">
          {[
            ["In", stats.checked_in_people],
            ["Expected", stats.registered_people],
            ["Not yet", stats.no_show_people ?? Math.max(0, (stats.registered_people || 0) - (stats.checked_in_people || 0))],
          ].map(([label, value]) => (
            <Card key={label}>
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-foreground">{value ?? 0}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
